import * as React from 'react';

import { BLOCK_CODE, BLOCK_CODE_LINE, code } from '@gitbook/doc-core';
import { Document, Value } from '@gitbook/slate';
import { Editor, type Plugin } from '@gitbook/slate-react';
import { useLiveRef, usePrevious } from '@gitbook/util-react-hooks';

import { DOMView } from '../DragAndDrop';
import { CodeBlock, type CodeBlockProps } from './CodeBlock';
import { CodeBlockLine } from './CodeBlockLine';
import { useSlateCodeHighlighter } from './syntax-highlighting';

export interface EditableCodeBlockProps extends Omit<CodeBlockProps, 'children'> {
    /** Text to edit in the code-block */
    value: string;

    /** Callback when the value changed */
    onChange: (value: string) => void;

    /** Syntax to use for highlighting */
    syntax: string;

    /** Code editor slate plugin  */
    plugins?: Plugin[];

    /** Callback when the editor is blurred */
    onBlur?: () => void;
}

/**
 * Editable code-block. Similar to a TextInput.
 */
export function EditableCodeBlock(props: EditableCodeBlockProps): React.ReactElement {
    const { value, syntax, plugins = [], onChange, onBlur, ...rest } = props;

    const [editorValue, setEditorValue] = React.useState(() => getValueFromCode(value, syntax));
    const editorValueRef = useLiveRef(editorValue);
    const { isFocused } = editorValue;

    React.useEffect(() => {
        if (getCodeText(editorValueRef.current) !== value && !isFocused) {
            setEditorValue(getValueFromCode(value, syntax));
        }
    }, [value, syntax, editorValueRef, isFocused]);

    const onBlurRef = useLiveRef(onBlur);
    const previousIsFocused = usePrevious(isFocused);
    React.useEffect(() => {
        if (previousIsFocused && !isFocused) {
            onBlurRef.current?.();
        }
    }, [isFocused, previousIsFocused, onBlurRef]);

    const highlightingPlugin = useSlateCodeHighlighter(editorValue.document, BLOCK_CODE);
    const allPlugins = React.useMemo<Plugin[]>(
        () => [
            ...plugins,
            highlightingPlugin,
            {
                renderNode: ({ node, parent, children, attributes }) => {
                    if (node.object === 'block' && node.type === BLOCK_CODE) {
                        return <DOMView {...attributes}>{children}</DOMView>;
                    }
                    if (node.object === 'block' && node.type === BLOCK_CODE_LINE) {
                        return (
                            <CodeBlockLine
                                number={parent.getChildIndex(node.key) + 1}
                                {...attributes}
                            >
                                {children}
                            </CodeBlockLine>
                        );
                    }
                },
            },
        ],
        [highlightingPlugin, plugins]
    );

    return (
        <CodeBlock {...rest}>
            <Editor
                value={editorValue}
                onChange={(change) => {
                    setEditorValue(change.value);

                    const textValue = getCodeText(change.value);
                    if (textValue !== value) {
                        onChange(textValue);
                    }
                }}
                plugins={allPlugins}
                spellCheck={false}
            />
        </CodeBlock>
    );
}

function getCodeText(value: Value): string {
    return value.document.getText('\n');
}

function getValueFromCode(value: string, syntax: string): Value {
    return Value.create({
        document: Document.create({
            nodes: [
                code.createCodeBlock(value, {
                    syntax,
                }),
            ],
        }),
    });
}
